/**
 * This file is part of the Haiman.
 * @link     : https://haiman.io/
 * @author   : Emil Zhai (root@derzh.com)
 * @modifier : Emil Zhai (root@derzh.com)
 * @copyright: Copyright (c) 2018 Hangzhou Haila Network Technology Co., Ltd.
 */

import { parseNavLocation } from '@/utils/navigation';
import { TIMSDK, COSSDK, TIMInstance, TIMMessageElement, TIMConversation, TIMMessage, TIMResponse } from './types/chat';

export const getTIMSDK = (() => {
  let TIM: TIMSDK;
  return (): Promise<TIMSDK> => {
    if (TIM) {
      return Promise.resolve(TIM);
    }
    return new Promise((resolve, reject) => {
      import('tim-js-sdk').then((d) => {
        TIM = d.default;
        resolve(TIM);
      }).catch(reject);
    });
  };
})();

export const getCOSSDK = (() => {
  let COS: COSSDK;
  return (): Promise<COSSDK> => {
    if (COS) {
      return Promise.resolve(COS);
    }
    return new Promise((resolve, reject) => {
      import('cos-js-sdk-v5').then((d) => {
        COS = d.default;
        resolve(COS);
      }).catch(reject);
    });
  };
})();

const getTIMInstance = (() => {
  const tims: Record<string, TIMInstance> = {};
  return (appid: number): Promise<TIMInstance> => {
    if (tims[appid]) {
      return Promise.resolve(tims[appid]);
    }
    return new Promise((resolve, reject) => {
      Promise.all<TIMSDK, COSSDK>([
        getTIMSDK(),
        getCOSSDK(),
      ]).then(([TIM, COS]) => {
        tims[appid] = TIM.create({ SDKAppID: appid });
        tims[appid].registerPlugin({ 'cos-js-sdk': COS });
        resolve(tims[appid]);
      }).catch(reject);
    });
  };
})();

const parseMessageElement = (item): TIMMessageElement | null => {
  let res: TIMMessageElement | null = null;
  if (item.type === 'TIMCustomElem') {
    try {
      const data = JSON.parse(item.content.data);
      if (data.type === 'template') {
        return {
          type: 'HMTemplateElem',
          data: Object.assign(data.data, {
            location: data.data.go
              ? parseNavLocation(data.data.go)
              : void 0,
          }),
          desc: item.content.description || item.desc || '[模板消息]',
          ext: item.content.extension,
        };
      }
      if (data.type === 'tips') {
        return {
          type: 'HMTipsElem',
          data: data.data,
          desc: item.content.description || item.desc || '[系统提示]',
          ext: item.content.extension,
        };
      }
      if (data.type === 'rejected') {
        return {
          type: 'HMRejectedElem',
          data: data.data,
          desc: item.content.description || item.desc || '[发送失败]',
          ext: item.content.extension,
        };
      }
    } catch (err) {}
    return {
      type: 'TIMCustomElem',
      data: item.content.data,
      desc: item.content.description || item.desc || '[自定义消息]',
      ext: item.content.extension,
    };
  }
  if (item.type === 'TIMImageElem') {
    const imageInfoArray = item.content.imageInfoArray;
    const thumb = imageInfoArray.find(v => v.sizeType === 3);
    const origin = imageInfoArray.find(v => v.sizeType === 1);
    res = {
      type: 'TIMImageElem',
      data: item || {},
      desc: item.desc || '[图片]',
      ext: item.extension,
    };
    if (thumb || origin) {
      res.data.thumb = (thumb || origin).url;
      res.data.origin = (origin || thumb).url;
    }
    return res;
  }
  if (item.type === 'TIMSoundElem') {
    return {
      type: 'TIMSoundElem',
      data: item.content,
      desc: item.desc || '[语音]',
      ext: item.extension,
    };
  }
  if (item.type === 'TIMTextElem') {
    return {
      type: 'TIMTextElem',
      data: item.content,
      desc: item.desc || '',
      ext: item.extension,
    };
  }
  console.warn('Unknown message element!');
  console.warn(item);
  return null;
};

const parseMessageElements = (list): TIMMessageElement[] => {
  const res: TIMMessageElement[] = [];
  list.forEach((p) => {
    const r = parseMessageElement(p);
    if (r) {
      res.push(r);
    }
  });
  return res;
};

const parseMessage = (msg): TIMMessage => {
  const elements = parseMessageElements(msg._elements);
  const element = elements[0] || { type: 'TIMTextElem', data: '[未知消息类型]' };
  return {
    id: msg.ID,
    clientSequence: msg.clientSequence,
    conversationID: msg.conversationID,
    conversationSubType: msg.conversationSubType,
    conversationType: msg.conversationType,
    element,
    elements,
    flow: msg.flow === 'out' || element.type === 'HMRejectedElem' ? 'out' : 'in',
    from: element.type === 'HMRejectedElem' ? msg.to : msg.from,
    to: element.type === 'HMRejectedElem' ? msg.from : msg.to,
    geo: msg.geo,
    isPlaceMessage: msg.isPlaceMessage === 1,
    isRead: msg.isRead === 1,
    isResend: msg.isResend === 1,
    isSystemMessage: msg.isSystemMessage,
    messagePriority: msg.messagePriority,
    protocol: msg.protocol,
    random: msg.random,
    sequence: msg.sequence,
    status: msg.status,
    time: msg.time,
  };
};

const getMessagePreview = (item: TIMMessageElement[]): string => item.map(elem => elem.desc).join();
const parseConversation = (p): TIMConversation => {
  const msg = p.lastMessage;
  const elements = parseMessageElements([{
    type: msg.type,
    content: msg.payload,
  }]);
  const element = elements[0] || { type: 'TIMTextElem', data: '[未知消息类型]' };
  return {
    conversationID: p.conversationID,
    lastMessage: {
      fromAccount: msg.fromAccount,
      lastSequence: msg.lastSequence,
      lastTime: msg.lastTime,
      messageForShow: getMessagePreview(elements) || msg.messageForShow,
      element,
      elements,
    },
    subType: p.subType,
    type: p.type,
    unreadCount: p.unreadCount,
    userProfile: p.userProfile,
  };
};

export const login = (options: {
  appid: number;
  userSig: string;
  userID: string;
  logLevel: number;
  on: Record<string, Function>;
}): Promise<unknown> => new Promise((resolve, reject) => {
  try {
    Promise.all([getTIMSDK(), getTIMInstance(options.appid)]).then(([TIM, tim]) => {
      Object.entries(options.on).forEach(([k, listener]) => {
        if (k === TIM.EVENT.MESSAGE_RECEIVED) {
          tim.on(k, ({ name, data }) => {
            listener({ name, data: data.map(msg => parseMessage(msg)) });
          });
        } else if (k === TIM.EVENT.CONVERSATION_LIST_UPDATED) {
          tim.on(k, ({ name, data }) => {
            listener({ name, data: data.map(p => parseConversation(p)) });
          });
        } else {
          tim.on(k, listener);
        }
      });
      tim.setLogLevel(options.logLevel);
      tim.login({ userSig: options.userSig, userID: options.userID }).then(resolve).catch(reject);
    }).catch(reject);
  } catch (e) {
    reject(e);
  }
});

export const logout = (appid: number): ReturnType<TIMInstance['logout']> => new Promise((resolve, reject) => {
  getTIMInstance(appid).then((tim) => {
    tim.logout().then(resolve).catch(reject);
  }).catch(reject);
});

const parseConversationID = (TIM: TIMSDK, conversationID: string): { conversationType: string; to: string } => {
  let conversationType!: string;
  let to!: string;
  if (conversationID.indexOf(TIM.TYPES.CONV_C2C) === 0) {
    conversationType = TIM.TYPES.CONV_C2C;
    to = conversationID.substr(TIM.TYPES.CONV_C2C.length);
  } else if (conversationID.indexOf(TIM.TYPES.CONV_GROUP) === 0) {
    conversationType = TIM.TYPES.CONV_GROUP;
    to = conversationID.substr(TIM.TYPES.CONV_GROUP.length);
  }
  return { conversationType, to };
};

const sendMessage = (
  appid: number,
  conversationID: string,
  messageGenerator: (tim: TIMInstance, conversationType: string, to: string) => TIMMessage,
): Promise<TIMResponse<TIMMessage>> => new Promise((resolve, reject) => {
  Promise.all([getTIMSDK(), getTIMInstance(appid)]).then(([TIM, tim]) => {
    const { conversationType, to } = parseConversationID(TIM, conversationID);
    if (conversationType && to) {
      const message = messageGenerator(tim, conversationType, to);
      tim.sendMessage(message).then((res) => {
        resolve({
          code: res.code,
          data: parseMessage(res.data.message),
        });
      }).catch(reject);
    } else {
      reject();
    }
  }).catch(reject);
});

export const sendTextMessage = (appid: number, conversationID: string, text: string): ReturnType<typeof sendMessage> =>
  sendMessage(appid, conversationID, (tim, conversationType, to) =>
    tim.createTextMessage({ to, conversationType, payload: { text } }));

export const sendImageMessage = (
  appid: number,
  conversationID: string,
  file: HTMLInputElement | object,
  onProgress?: (percentage: number) => void,
): ReturnType<typeof sendMessage> =>
  sendMessage(appid, conversationID, (tim, conversationType, to) =>
    tim.createImageMessage({ to, conversationType, payload: { file }, onProgress }));

export const sendFileMessage = (
  appid: number,
  conversationID: string,
  file: HTMLInputElement,
  onProgress?: (percentage: number) => void,
): ReturnType<typeof sendMessage> =>
  sendMessage(appid, conversationID, (tim, conversationType, to) =>
    tim.createFileMessage({ to, conversationType, payload: { file }, onProgress }));

export const getMessageList = (appid: number, conversationID, limit, nextReqMessageID): Promise<{
  code: string;
  data: TIMMessage[];
  point?: string;
}> => new Promise((resolve, reject) => {
  getTIMInstance(appid).then((tim) => {
    tim.getMessageList({
      conversationID,
      nextReqMessageID,
      count: limit,
    }).then((res) => {
      resolve({
        code: res.code,
        data: res.data.messageList.map(msg => parseMessage(msg)),
        point: res.data.isCompleted ? void 0 : res.data.nextReqMessageID,
      });
    }).catch(reject);
  }).catch(reject);
});

export const setMessageRead = (appid: number, cid: string): void => {
  getTIMInstance(appid).then((tim) => {
    tim.setMessageRead({ conversationID: cid });
  });
};

export const getConversationList = (appid: number): Promise<TIMResponse<TIMConversation[]>> => new Promise((resolve, reject) => {
  getTIMInstance(appid).then((tim) => {
    tim.getConversationList().then((res) => {
      const conversations: TIMConversation[] = [];
      res.data.conversationList.forEach(p => parseConversation(p));
      resolve({ code: res.code, data: conversations });
    }).catch(reject);
  }).catch(reject);
});

export const deleteConversation = (appid: number, conversationID: string): ReturnType<TIMInstance['deleteConversation']> => new Promise((resolve, reject) => {
  getTIMInstance(appid).then((tim) => {
    tim.deleteConversation(conversationID).then((res) => {
      resolve(res);
    }).catch(reject);
  }).catch(reject);
});

export const getBlacklist = (appid: number): ReturnType<TIMInstance['getBlacklist']> => new Promise((resolve, reject) => {
  getTIMInstance(appid).then((tim) => {
    tim.getBlacklist().then((res) => {
      resolve(res);
    }).catch(reject);
  }).catch(reject);
});

export const addToBlacklist = (appid: number, uids: string[]): ReturnType<TIMInstance['addToBlacklist']> => new Promise((resolve, reject) => {
  getTIMInstance(appid).then((tim) => {
    tim.addToBlacklist({ userIDList: uids }).then((res) => {
      resolve(res);
    }).catch(reject);
  }).catch(reject);
});

export const removeFromBlacklist = (appid: number, uids: string[]): ReturnType<TIMInstance['removeFromBlacklist']> => new Promise((resolve, reject) => {
  getTIMInstance(appid).then((tim) => {
    tim.removeFromBlacklist({ userIDList: uids }).then((res) => {
      resolve(res);
    }).catch(reject);
  }).catch(reject);
});
